import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getProductImage } from "../../data/images";

const ease = [0.16, 1, 0.3, 1];

// Kinetic hero: masked line-by-line headline reveal, parallax muse portrait
// and a floating real bracelet card that drifts against the scroll.
export const Hero = ({ product }) => {
  const braceletImg = product?.image ? getProductImage(product.image, product.name) : null;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-brand-cream" data-testid="hero-section">
      <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-[62%]">
        <motion.div style={{ y: imgY }} className="h-[62vh] min-h-[420px] w-full lg:h-full">
          <motion.picture
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.8, ease }}
            className="block h-full w-full"
          >
            <source media="(min-width: 1024px)" srcSet="/brand/heena-hero-desktop.jpg" />
            <motion.img
              src="/brand/heena-hero-mobile.jpg"
              alt="Heena, the HESHAURA muse, wearing a handcrafted HESHAURA bracelet"
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.8, ease }}
              className="h-full w-full object-cover object-[50%_20%] lg:object-[42%_22%]"
              data-testid="hero-image"
              fetchPriority="high"
            />
          </motion.picture>
        </motion.div>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-brand-cream to-transparent lg:hidden" />
        <div className="hidden lg:absolute lg:inset-0 lg:block lg:bg-gradient-to-r lg:from-brand-cream lg:via-brand-cream/55 lg:via-25% lg:to-transparent lg:to-55%" />
        <div className="hidden lg:absolute lg:inset-y-0 lg:right-0 lg:block lg:w-24 lg:bg-gradient-to-l lg:from-brand-cream/40 lg:to-transparent" />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.4 }}
          className="absolute bottom-16 right-8 hidden font-serif text-2xl italic leading-snug text-brand-cream/90 drop-shadow-md lg:block xl:text-3xl"
          aria-hidden="true"
        >
          Your story,
          <br />
          on your wrist
        </motion.p>
      </div>

      <div className="relative mx-auto flex max-w-7xl px-5 pb-20 pt-10 sm:px-6 sm:pt-12 lg:min-h-[88vh] lg:items-center lg:px-8 lg:py-24">
        <div className="max-w-xl lg:max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease }}
            className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-gold"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-brand-gold/70" aria-hidden="true" />
            Timeless Wristwear · India
          </motion.p>
          <h1
            className="mt-7 font-serif text-5xl font-medium leading-[1.04] tracking-tight text-brand-charcoal sm:text-6xl lg:text-7xl"
            data-testid="hero-heading"
          >
            {["Bracelets", "made to be", "remembered"].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.35 + i * 0.14, ease }}
                >
                  {i === 2 ? <em className="font-normal italic text-brand-gold">remembered</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease }}
            className="mt-7 max-w-md text-base font-light leading-relaxed tracking-wide text-brand-stone sm:text-lg"
            data-testid="hero-subtitle"
          >
            More than an accessory — a feeling, a story, a more radiant you.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease }}
            className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-button"
              className="btn-shimmer group inline-flex items-center justify-center gap-3 bg-brand-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-brand-cream shadow-[0_12px_30px_-12px_rgba(176,148,96,0.6)] transition-colors duration-300 hover:bg-brand-gold-hover"
            >
              Shop Bracelets
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#collections"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center border border-brand-charcoal/30 px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-brand-charcoal transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold"
            >
              The Bracelet Edit
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-brand-stone"
          >
            <span className="inline-flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-brand-gold" aria-hidden="true" />
              Complimentary shipping
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-brand-gold" aria-hidden="true" />
              Hand-finished in small batches
            </span>
            <span className="inline-flex items-center gap-2 text-brand-gold">
              <span className="h-1 w-1 rounded-full bg-brand-gold" aria-hidden="true" />
              33% off your first order
            </span>
          </motion.div>
        </div>
      </div>

      {braceletImg && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease }}
          style={{ y: cardY }}
          className="absolute bottom-8 right-6 hidden w-44 border border-brand-gold/40 bg-white/90 p-2 shadow-[0_30px_60px_-30px_rgba(28,25,23,0.45)] backdrop-blur-md lg:block xl:w-52"
          data-testid="hero-bracelet-card"
        >
          <Link to={`/product/${product.slug}`} aria-label={`View ${product.name}`}>
            <img src={braceletImg.src} alt={`${product.name} — handcrafted HESHAURA bracelet`} className="aspect-square w-full object-cover" />
            <p className="px-1 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold">Latest bracelet</p>
            <p className="truncate px-1 pb-1 font-serif text-sm text-brand-charcoal">{product.name}</p>
          </Link>
        </motion.div>
      )}
    </section>
  );
};
